"use client";
import React, { useState } from "react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";
import emitter from "@packages/lib/eventEmitter/eventEmitter";
import { DataLayerGA4AttrType, GA4EventAttrType } from "@packages/lib/types/datalayerGA";
import {
  GA4DataLayer,
  GA4DataLayerFn,
  getCookie,
} from "@packages/lib/utlils/helper-function";
import { usePathname } from "next/navigation";
import { GAConstants } from "@packages/shared-components/services/constants";

const UcasComponent = dynamic(
  () =>
    import("@packages/shared-components/common-utilities/popups/ucas-calculator/ucascomponent"),
  { ssr: false }
);

const GradeBanner = ({
  gaData,
  collegeCount,
  gaOldData,
}: {
  gaData: GA4EventAttrType;
  collegeCount: number;
  gaOldData?: DataLayerGA4AttrType;
}) => {
  const pathName = usePathname();
  const searchparams = useSearchParams();
  const filterCookie = JSON.parse(
    decodeURIComponent(
      (typeof document !== "undefined" &&
        document.cookie.split("filter_param=")[1]?.split(";")[0]) ||
        "{}"
    )
  );
  const location = searchparams?.get("location");
  const score =
    searchparams?.get("score") || (filterCookie?.score && document.referrer);
  const [isUcasPopupOpen, setUcasPopupOpen] = useState(false);
  const isWhatuni = process.env.PROJECT === "Whatuni";
  const isPGS = process.env.PROJECT === "PGS";
  const showLocation =
    score || (isPGS && !location) || pathName.includes("postgraduate-courses");

  const getBannerGAData = (bannerType: string): GA4EventAttrType => {
    const gradeAppliedList = JSON.parse(
      decodeURIComponent(getCookie("UCAS") || "{}")
    );
    const gradeApplied = gradeAppliedList?.userStudyLevelEntry
      ?.map((gradeItems: { SelectedLevel: any }) => gradeItems?.SelectedLevel)
      ?.join("|");
    return {
      ...gaData,
      event: GAConstants.track_event,
      event_name: "filter_panel_interaction",
      click_text:
        bannerType === "location"
          ? "add my location"
          : bannerType === "ucas"
            ? "Add my grades"
            : "",
      destination_region_city: location ?? "NA",
    };
  };
  const getBannerOldGAData = (bannerType: string): DataLayerGA4AttrType => {
    const gradeAppliedList = JSON.parse(
      decodeURIComponent(getCookie("UCAS") || "{}")
    );
    const gradeApplied = gradeAppliedList?.userStudyLevelEntry
      ?.map((gradeItems: { SelectedLevel: any }) => gradeItems?.SelectedLevel)
      ?.join("|");
    return {
      ...gaOldData,
      event: "ga_events",
      eventName: isWhatuni ? "filter_clicks" : "filters",
      data_label:bannerType === "ucas"
            ? "grade_filter"
            : "location",
      destination_country: location ?? "NA",
    };
  };

  const handleBodyOverflow = (add: boolean) => {
    const body = document?.body;
    body?.classList[add ? "add" : "remove"]("overflow-y-hidden");
  };

  const filterEvents = (eventName: string | null | undefined) => {
    GA4DataLayerFn(getBannerOldGAData(eventName ?? ""));
    GA4DataLayer(getBannerGAData(eventName ?? ""));
    emitter.emit("isfilterOpen", eventName);
  };

  const ucasClick = (bannerType: string) => {
    GA4DataLayerFn(getBannerOldGAData(bannerType));
    GA4DataLayer(getBannerGAData(bannerType));
    setUcasPopupOpen(true);
    handleBodyOverflow(true);
  };

  const ucasClose = () => {
    setUcasPopupOpen(false);
    handleBodyOverflow(false);
  };

  const bannerText = {
    title: isWhatuni
      ? `Add your ${showLocation ? "location" : "grades"}`
      : "Find universities near you",
    description: showLocation
      ? isWhatuni
        ? "Add your location to discover universities nearby or in regions you're interested in"
        : "Add your location to explore options near you or anywhere you prefer"
      : "Add your UCAS points to help tailor your search to find the right uni for you",
  };

  return (
    <>
      <div
        className={`${isWhatuni ? "bg-blue-100" : "bg-positive-light"} ${collegeCount > 1 ? "mt-[16px]" : "my-[16px]"} p-[16px] rounded-[8px] flex flex-col gap-[16px] lg:flex-row lg:justify-between`}
      >
        <div className="flex gap-[16px]">
          <div
            className={`bg-blue-200 text-grey900 rounded-tl-[24px] rounded-br-[24px] p-[8px] w-[48px] h-[48px] flex items-center justify-center shrink-0 ${isPGS ? "bg-secondary-300 hidden lg:block" : ""}`}
          >
            {isWhatuni && (
              <>
                {!score ? (
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M11.6665 8.77778H20.3331M20.3331 23.2222V18.8889M15.9998 23.2222H16.0142M11.6665 23.2222H11.6809M11.6665 18.8889H11.6809M15.9998 18.8889H16.0142M20.3331 14.5556H20.3476M15.9998 14.5556H16.0142M11.6665 14.5556H11.6809M8.77756 29H23.222C24.8175 29 26.1109 27.7066 26.1109 26.1111V5.88889C26.1109 4.2934 24.8175 3 23.222 3H8.77756C7.18207 3 5.88867 4.2934 5.88867 5.88889V26.1111C5.88867 27.7066 7.18207 29 8.77756 29Z"
                      stroke="#0657AD"
                      strokeWidth="2.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg
                    width="32"
                    height="32"
                    viewBox="0 0 32 32"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 16L5.88889 13.1111M5.88889 13.1111L16 3L26.1111 13.1111M5.88889 13.1111V27.5556C5.88889 28.3533 6.53559 29 7.33333 29H11.6667M26.1111 13.1111L29 16M26.1111 13.1111V27.5556C26.1111 28.3533 25.4644 29 24.6667 29H20.3333M11.6667 29C12.4644 29 13.1111 28.3533 13.1111 27.5556V21.7778C13.1111 20.98 13.7578 20.3333 14.5556 20.3333H17.4444C18.2422 20.3333 18.8889 20.98 18.8889 21.7778V27.5556C18.8889 28.3533 19.5356 29 20.3333 29M11.6667 29H20.3333"
                      stroke="#0657AD"
                      strokeWidth="2.67"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </>
            )}

            {isPGS && (
              <svg
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 16L5.88889 13.1111M5.88889 13.1111L16 3L26.1111 13.1111M5.88889 13.1111V27.5556C5.88889 28.3533 6.53559 29 7.33333 29H11.6667M26.1111 13.1111L29 16M26.1111 13.1111V27.5556C26.1111 28.3533 25.4644 29 24.6667 29H20.3333M11.6667 29C12.4644 29 13.1111 28.3533 13.1111 27.5556V21.7778C13.1111 20.98 13.7578 20.3333 14.5556 20.3333H17.4444C18.2422 20.3333 18.8889 20.98 18.8889 21.7778V27.5556C18.8889 28.3533 19.5356 29 20.3333 29M11.6667 29H20.3333"
                  stroke="#1E8782"
                  strokeWidth="2.67"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <div className="flex flex-col gap-[4px]">
            <div className="para-lg font-bold font-farro">
              {bannerText.title}
            </div>
            <div className="small">{bannerText.description}</div>
          </div>
        </div>
        <button
          type="button"
          onClick={() =>
            showLocation ? filterEvents("location") : ucasClick("ucas")
          }
          className="flex items-center justify-center self-center gap-[8px] btn btn-primary px-[20px] py-[10px] w-full lg:w-fit"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              fillRule="evenodd"
              clipRule="evenodd"
              d="M10 1.875C10.641 1.875 11.1607 2.39467 11.1607 3.03571V8.83929H16.9643C17.6053 8.83929 18.125 9.35895 18.125 10C18.125 10.641 17.6053 11.1607 16.9643 11.1607H11.1607V16.9643C11.1607 17.6053 10.641 18.125 10 18.125C9.35895 18.125 8.83929 17.6053 8.83929 16.9643V11.1607H3.03571C2.39467 11.1607 1.875 10.641 1.875 10C1.875 9.35895 2.39467 8.83928 3.03571 8.83928L8.83929 8.83929V3.03571C8.83929 2.39467 9.35895 1.875 10 1.875Z"
              fill="#F9FAFB"
            />
          </svg>
          Add my {showLocation ? "location" : "grades"}
        </button>
      </div>
      {isUcasPopupOpen && (
        <UcasComponent onClose={ucasClose} isUcasOpen={isUcasPopupOpen} />
      )}
    </>
  );
};

export default GradeBanner;
